/**
 * Team Calendar — month grid view of staff leaves & Malaysia public holidays.
 * Designed for HR/Finance/Director to see team availability at a glance.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Loader, X, Users,
  Plane, Heart, Zap, Coins, Baby, MoreHorizontal,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================
// Leave-type styling
// ============================================================
const LEAVE_TYPE_META = {
  annual:    { label: 'Tahunan',    color: 'bg-blue-500',    textColor: 'text-blue-700',    bgLight: 'bg-blue-50',    icon: Plane },
  mc:        { label: 'MC',         color: 'bg-rose-500',    textColor: 'text-rose-700',    bgLight: 'bg-rose-50',    icon: Heart },
  emergency: { label: 'Emergency',  color: 'bg-amber-500',   textColor: 'text-amber-700',   bgLight: 'bg-amber-50',   icon: Zap },
  unpaid:    { label: 'Tanpa Gaji', color: 'bg-gray-500',    textColor: 'text-gray-700',    bgLight: 'bg-gray-100',   icon: Coins },
  maternity: { label: 'Bersalin',   color: 'bg-pink-500',    textColor: 'text-pink-700',    bgLight: 'bg-pink-50',    icon: Baby },
  paternity: { label: 'Paterniti',  color: 'bg-indigo-500',  textColor: 'text-indigo-700',  bgLight: 'bg-indigo-50',  icon: Baby },
  other:     { label: 'Lain-lain',  color: 'bg-slate-500',   textColor: 'text-slate-700',   bgLight: 'bg-slate-50',   icon: MoreHorizontal },
};

const getMeta = (type) => LEAVE_TYPE_META[type] || LEAVE_TYPE_META.other;

const MONTH_NAMES = [
  'Januari', 'Februari', 'Mac', 'April', 'Mei', 'Jun',
  'Julai', 'Ogos', 'September', 'Oktober', 'November', 'Disember',
];

// ============================================================
// Day cell — compact for mobile, expandable on click
// ============================================================
const DayCell = ({ day, onClick, isToday }) => {
  const dayNum = parseInt(day.date.slice(8, 10), 10);
  const totalLeaves = day.leaves_approved.length + day.leaves_pending.length;

  // Count by type for badge stacking
  const typeCounts = useMemo(() => {
    const counts = {};
    day.leaves_approved.forEach((l) => {
      counts[l.leave_type] = (counts[l.leave_type] || 0) + 1;
    });
    return counts;
  }, [day]);

  const isHoliday = !!day.holiday;
  const isWeekend = day.is_weekend;

  return (
    <button
      onClick={() => onClick(day)}
      data-testid={`day-${day.date}`}
      className={`relative aspect-square min-h-[64px] p-1.5 rounded-lg border text-left transition-all hover:shadow-md hover:scale-[1.02] active:scale-95 ${
        isToday ? 'ring-2 ring-red-600 border-red-300' : 'border-gray-200'
      } ${
        isHoliday ? 'bg-red-50 border-red-200' :
        isWeekend ? 'bg-gray-50' :
        'bg-white'
      }`}
    >
      <div className="flex items-start justify-between">
        <span className={`text-sm font-bold ${
          isToday ? 'text-red-700' :
          isHoliday ? 'text-red-700' :
          isWeekend ? 'text-gray-400' :
          'text-gray-900'
        }`}>
          {dayNum}
        </span>
        {totalLeaves > 0 && (
          <span className="text-[10px] font-bold bg-gray-900 text-white rounded-full w-4 h-4 flex items-center justify-center">
            {totalLeaves}
          </span>
        )}
      </div>

      {/* Holiday name (truncated) */}
      {isHoliday && (
        <p className="text-[9px] text-red-700 font-medium leading-tight mt-0.5 line-clamp-2">
          {day.holiday}
        </p>
      )}

      {/* Leave-type dots stack */}
      {totalLeaves > 0 && !isHoliday && (
        <div className="flex flex-wrap gap-0.5 mt-1">
          {Object.entries(typeCounts).slice(0, 4).map(([type, count]) => {
            const meta = getMeta(type);
            return (
              <span
                key={type}
                className={`${meta.color} text-white text-[8px] font-bold rounded px-1 leading-none py-[2px]`}
                title={`${meta.label}: ${count}`}
              >
                {count}
              </span>
            );
          })}
        </div>
      )}
    </button>
  );
};

// ============================================================
// Day Detail Modal
// ============================================================
const DayDetailModal = ({ day, onClose }) => {
  if (!day) return null;
  const dateObj = new Date(day.date + 'T00:00:00');
  const dateStr = dateObj.toLocaleDateString('ms-MY', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-md p-5 shadow-xl space-y-3 max-h-[85vh] overflow-y-auto" data-testid="day-detail-modal">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-800">{dateStr}</h3>
            {day.holiday && (
              <span className="inline-block mt-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded-full">
                🇲🇾 {day.holiday}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="close-modal-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {day.leaves_approved.length === 0 && day.leaves_pending.length === 0 && !day.holiday && (
          <p className="text-sm text-gray-500 text-center py-4">Tiada cuti dijadualkan.</p>
        )}

        {day.leaves_approved.length > 0 && (
          <div>
            <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-2">
              Approved ({day.leaves_approved.length})
            </p>
            <div className="space-y-1.5">
              {day.leaves_approved.map((l) => {
                const meta = getMeta(l.leave_type);
                const Icon = meta.icon;
                return (
                  <div key={l.id} className={`flex items-center gap-2.5 p-2.5 rounded-lg ${meta.bgLight}`}>
                    <Icon className={`w-4 h-4 ${meta.textColor} shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{l.staff_name}</p>
                      <p className={`text-[11px] font-medium ${meta.textColor}`}>
                        {meta.label}{l.department ? ` • ${l.department}` : ''}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {day.leaves_pending.length > 0 && (
          <div>
            <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2">
              Pending Approval ({day.leaves_pending.length})
            </p>
            <div className="space-y-1.5">
              {day.leaves_pending.map((l) => {
                const meta = getMeta(l.leave_type);
                return (
                  <div key={l.id} className="flex items-center gap-2.5 p-2.5 rounded-lg bg-amber-50 border border-dashed border-amber-300">
                    <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{l.staff_name}</p>
                      <p className="text-[11px] text-amber-700">
                        {meta.label}{l.department ? ` • ${l.department}` : ''}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Main Page
// ============================================================
const TeamCalendarPage = () => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [monthIdx, setMonthIdx] = useState(today.getMonth());  // 0-indexed
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  const monthStr = `${year}-${String(monthIdx + 1).padStart(2, '0')}`;

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/hr/team-calendar`, { params: { month: monthStr } });
      setData(data);
    } catch (e) {
      console.error('Calendar fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [monthStr]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const goPrevMonth = () => {
    if (monthIdx === 0) { setYear((y) => y - 1); setMonthIdx(11); }
    else setMonthIdx((m) => m - 1);
  };
  const goNextMonth = () => {
    if (monthIdx === 11) { setYear((y) => y + 1); setMonthIdx(0); }
    else setMonthIdx((m) => m + 1);
  };
  const goToday = () => { setYear(today.getFullYear()); setMonthIdx(today.getMonth()); };

  // Build a 6-row × 7-col grid (with leading/trailing nulls for proper month layout)
  const grid = useMemo(() => {
    if (!data?.days?.length) return [];
    const firstDate = new Date(data.days[0].date + 'T00:00:00');
    const startWeekday = (firstDate.getDay() + 6) % 7; // Mon=0, Sun=6 (Malaysia week starts Mon)
    const cells = Array(startWeekday).fill(null).concat(data.days);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [data]);

  const todayIso = today.toISOString().slice(0, 10);

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="team-calendar-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <CalendarIcon className="w-6 h-6 text-red-700" /> Kalendar Pasukan
          </h1>
          <p className="text-gray-500 text-xs sm:text-sm">
            Tengok cuti staff & cuti umum Malaysia dalam satu tempat.
          </p>
        </div>
        {data?.summary && (
          <div className="flex items-center gap-2 text-xs">
            <span className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg font-semibold">
              <Users className="w-3 h-3 inline-block mr-1 -mt-0.5" />
              {data.summary.total_staff} staff aktif
            </span>
            <span className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg font-semibold">
              {data.summary.unique_leaves} cuti bulan ini
            </span>
          </div>
        )}
      </div>

      {/* Month Navigator */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 p-2 sm:p-3">
        <button onClick={goPrevMonth} data-testid="prev-month-btn"
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <h2 className="text-base sm:text-xl font-bold text-gray-800">
            {MONTH_NAMES[monthIdx]} {year}
          </h2>
          <button onClick={goToday} data-testid="today-btn"
            className="px-2.5 py-1 text-xs font-semibold bg-red-50 text-red-700 rounded-md hover:bg-red-100">
            Hari Ini
          </button>
        </div>
        <button onClick={goNextMonth} data-testid="next-month-btn"
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Legend (leave types) */}
      <div className="flex flex-wrap gap-2 text-xs">
        {Object.entries(LEAVE_TYPE_META).map(([key, meta]) => (
          <span key={key} className="inline-flex items-center gap-1.5 px-2 py-1 bg-white border border-gray-200 rounded-md">
            <span className={`w-2.5 h-2.5 rounded-full ${meta.color}`} />
            <span className="text-gray-700">{meta.label}</span>
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-red-50 border border-red-200 rounded-md">
          <span className="w-2.5 h-2.5 rounded-full bg-red-300" />
          <span className="text-red-700 font-medium">Cuti Umum</span>
        </span>
      </div>

      {/* Calendar Grid */}
      {loading ? (
        <div className="bg-white rounded-2xl p-12 flex items-center justify-center text-gray-400">
          <Loader className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 p-2 sm:p-4 overflow-x-auto">
          {/* Weekday header */}
          <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2 min-w-[440px]">
            {['Isn', 'Sel', 'Rab', 'Kha', 'Jum', 'Sab', 'Ahd'].map((d) => (
              <div key={d} className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase text-center py-1">{d}</div>
            ))}
          </div>
          {/* Days grid */}
          <div className="grid grid-cols-7 gap-1 sm:gap-2 min-w-[440px]">
            {grid.map((day, idx) => (
              day ? (
                <DayCell key={day.date} day={day} onClick={setSelectedDay} isToday={day.date === todayIso} />
              ) : (
                <div key={`empty-${idx}`} className="aspect-square min-h-[64px]" />
              )
            ))}
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedDay && <DayDetailModal day={selectedDay} onClose={() => setSelectedDay(null)} />}
    </div>
  );
};

export default TeamCalendarPage;
